import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

/**
 * Visual tabs (what the user sees, left-to-right).
 * The underlying page uses URL indices 0, 3, 4, 5, 6, 7 (indices 1 & 2 are
 * commented-out legacy tabs), so we map URL index → visual position.
 */
const VISUAL_TABS = ['General', 'Team & Access', 'Roles', 'Profile', 'Language', 'Login Config'];
const URL_TO_VISUAL = { 0: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5 };

const SettingsHeader = () => (
  <div className="flex flex-col gap-2 mb-4 sm:mb-6">
    <SkBar h="h-7" w="w-40" className="max-w-full" />
    <SkBar h="h-3" w="w-80" className="max-w-full" />
  </div>
);

const IconTab = ({ active = false, labelW = 'w-16' }) => (
  <div
    className={`pb-2 flex items-center gap-2 flex-shrink-0 ${
      active ? 'border-b-2 border-gray-300' : ''
    }`}
  >
    <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
    <SkBar h="h-4" w={labelW} />
  </div>
);

const TabsRow = ({ active = 0 }) => {
  const labelWidths = ['w-16', 'w-24', 'w-12', 'w-14', 'w-16', 'w-20'];
  return (
    <div className="flex gap-6 sm:gap-8 overflow-x-auto">
      {VISUAL_TABS.map((_, i) => (
        <IconTab key={i} active={active === i} labelW={labelWidths[i]} />
      ))}
    </div>
  );
};

/* ─────────── Shared primitives ─────────── */

const SectionHeader = () => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    <SkBar h="h-5" w="w-48" />
  </div>
);

const InputField = () => <SkBar h="h-10" w="w-full" className="rounded-md" />;

/* ─────────── General tab ─────────── */

const CompanyInfoCard = () => (
  <Card className="shadow-sm mb-4 sm:mb-6" styles={{ body: { padding: 24 } }}>
    <SectionHeader />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <InputField />
      <InputField />
      <InputField />
      <InputField />
    </div>
  </Card>
);

const PreferenceToggleRow = ({ tone = 'blue' }) => {
  const palette =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50'
      : 'border-blue-200 bg-blue-50';
  return (
    <div className={`flex items-center justify-between gap-3 p-4 border rounded-lg mb-3 ${palette}`}>
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-4" w="w-44" />
        <SkBar h="h-3" w="w-72" className="max-w-full" />
      </div>
      <SkBar
        h="h-6"
        w="w-12"
        className={`rounded-full flex-shrink-0 ${tone !== 'amber' ? 'bg-gray-300' : ''}`}
      />
    </div>
  );
};

const SystemPreferencesCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 24 } }}>
    <SectionHeader />
    <PreferenceToggleRow tone="blue" />
    <PreferenceToggleRow tone="blue" />
    <PreferenceToggleRow tone="amber" />
    <div className="flex justify-end mt-6">
      <SkBar h="h-10" w="w-36" className="rounded-md" />
    </div>
  </Card>
);

const GeneralTabContent = () => (
  <>
    <CompanyInfoCard />
    <SystemPreferencesCard />
  </>
);

/* ─────────── Team & Access tab ─────────── */

const TEAM_COLS = ['w-12', 'w-14', 'w-12', 'w-14', 'w-14', 'w-16'];

const TeamAccessRow = () => (
  <div className="grid grid-cols-[2fr_2fr_1.2fr_1fr_0.8fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* User: avatar + name */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-3" w="w-40" />
    </div>
    {/* Email */}
    <SkBar h="h-3" w="w-48" />
    {/* Role pill */}
    <SkBar h="h-6" w="w-24" className="rounded-md" />
    {/* Status pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md" />
    {/* Branch */}
    <SkBar h="h-3" w="w-10" />
    {/* Actions: eye + edit + delete */}
    <div className="flex items-center gap-2 justify-end">
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
    </div>
  </div>
);

const TeamAccessTabContent = () => (
  <>
    {/* Search + Add Employee row (outside the table card) */}
    <div className="flex gap-3 mb-4">
      <SkBar h="h-10" w="w-full" className="rounded-md" />
      <SkBar h="h-10" w="w-40" className="rounded-md flex-shrink-0" />
    </div>

    {/* Users table card */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-[2fr_2fr_1.2fr_1fr_0.8fr_1fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
            {TEAM_COLS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === TEAM_COLS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <TeamAccessRow key={i} />
          ))}
        </div>
      </div>
    </Card>
  </>
);

/* ─────────── Roles tab ─────────── */

const ROLES_COLS = ['w-12', 'w-12', 'w-20', 'w-16', 'w-14', 'w-16'];

const RoleRow = () => (
  <div className="grid grid-cols-[2fr_1.5fr_2fr_1fr_1fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Role: shield icon + name + level */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-2" w="w-14" />
      </div>
    </div>
    {/* Type: 1-2 pills */}
    <div className="flex items-center gap-2">
      <SkBar h="h-5" w="w-16" className="rounded-md" />
      <SkBar h="h-5" w="w-14" className="rounded-md" />
    </div>
    {/* Description */}
    <SkBar h="h-3" w="w-40" />
    {/* Modules pill */}
    <SkBar h="h-5" w="w-20" className="rounded-md" />
    {/* Status pill */}
    <SkBar h="h-5" w="w-14" className="rounded-md" />
    {/* Actions: edit + copy + delete */}
    <div className="flex items-center gap-2 justify-end">
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
    </div>
  </div>
);

const RolesTabContent = () => (
  <>
    {/* Refresh + Create Role row (right-aligned, outside table card) */}
    <div className="flex items-center justify-end gap-2 mb-4">
      <SkBar h="h-10" w="w-28" className="rounded-md" />
      <SkBar h="h-10" w="w-32" className="rounded-md" />
    </div>

    {/* Roles table card */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <div className="overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-[2fr_1.5fr_2fr_1fr_1fr_1fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
            {ROLES_COLS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === ROLES_COLS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <RoleRow key={i} />
          ))}
        </div>
      </div>
    </Card>
  </>
);

/* ─────────── Profile tab ─────────── */

const ProfileDetailCell = ({ label = true }) => (
  <div className={`px-4 py-4 border-r border-gray-200 last:border-r-0 ${label ? 'bg-gray-50/60' : ''}`}>
    <SkBar h="h-3" w={label ? 'w-28' : 'w-40'} />
  </div>
);

const ProfileDetailRow = ({ cells = 6 }) => (
  <div className="grid grid-cols-2 md:grid-cols-6 border-b border-gray-200 last:border-b-0">
    {Array.from({ length: cells }).map((_, i) => (
      <ProfileDetailCell key={i} label={i % 2 === 0} />
    ))}
  </div>
);

const AdminProfileCard = () => (
  <Card className="shadow-sm mb-4 sm:mb-6" styles={{ body: { padding: 24 } }}>
    {/* Admin Profile header: icon + title */}
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-40" />
    </div>

    {/* Profile Information sub-header + Edit Profile link */}
    <div className="flex items-center justify-between gap-3 mb-4">
      <SkBar h="h-4" w="w-44" />
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
        <SkBar h="h-4" w="w-24" />
      </div>
    </div>

    {/* Detail grid */}
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <ProfileDetailRow />
      <ProfileDetailRow />
      <ProfileDetailRow />
      <ProfileDetailRow cells={4} />
    </div>
  </Card>
);

const PreferencesCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 24 } }}>
    {/* Preferences header: icon + title */}
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-32" />
    </div>

    {/* Two dropdowns side by side with wide gap */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
      <SkBar h="h-10" w="w-full md:w-52" className="rounded-md" />
      <SkBar h="h-10" w="w-full md:w-52" className="rounded-md" />
    </div>

    {/* Read-only hint */}
    <SkBar h="h-3" w="w-[28rem]" className="max-w-full" />
  </Card>
);

const ProfileTabContent = () => (
  <>
    <AdminProfileCard />
    <PreferencesCard />
  </>
);

/* ─────────── Language tab ─────────── */

const SubTabButton = ({ active = false }) => (
  <div
    className={`flex items-center gap-2 px-4 py-2 rounded-md ${
      active ? 'bg-white border border-gray-200 shadow-sm' : ''
    }`}
  >
    <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
    <SkBar h="h-4" w="w-10" />
  </div>
);

const LanguageRow = () => (
  <div className="grid grid-cols-[1fr_1.5fr_0.2fr] gap-3 py-3 border-b border-gray-100 last:border-b-0 items-center">
    <SkBar h="h-3" w="w-48" />
    <SkBar h="h-10" w="w-full" className="rounded-md" />
    <SkBar h="h-6" w="w-6" className="rounded-md justify-self-end" />
  </div>
);

const LanguageTabContent = () => (
  <>
    <div className="flex items-center gap-2 mb-4">
      <SubTabButton active />
      <SubTabButton />
    </div>

    <div className="flex gap-3 mb-4">
      <SkBar h="h-10" w="w-full" className="rounded-md" />
      <SkBar h="h-10" w="w-32" className="rounded-md flex-shrink-0" />
      <SkBar h="h-10" w="w-20" className="rounded-md flex-shrink-0" />
    </div>

    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-[1fr_1.5fr_0.2fr] gap-3 pb-3 border-b border-gray-200">
            <SkBar h="h-4" w="w-28" />
            <SkBar h="h-4" w="w-24" />
            <span />
          </div>
          {Array.from({ length: 12 }).map((_, i) => (
            <LanguageRow key={i} />
          ))}
        </div>
      </div>
    </Card>
  </>
);

/* ─────────── Login Config tab ─────────── */

const LoginConfigTabContent = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 24 } }}>
    {/* Header: icon + title */}
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-48" />
    </div>

    {/* Half-width bordered panel: title + description + badge + toggle */}
    <div className="max-w-md p-4 border border-gray-200 rounded-lg flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-4" w="w-32" />
        <SkBar h="h-3" w="w-60" className="max-w-full" />
        <SkBar h="h-5" w="w-20" className="rounded-md mt-1" />
      </div>
      <SkBar
        h="h-6"
        w="w-12"
        className="rounded-full flex-shrink-0 bg-gray-300"
      />
    </div>

    {/* App Version card: header + two platform panels with two inputs each + save */}
    <div className="flex items-center gap-3 mt-8 mb-6">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-32" />
    </div>
    <SkBar h="h-4" w="w-96" className="max-w-full mb-6" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {[0, 1].map((i) => (
        <div key={i} className="p-4 border border-gray-200 rounded-lg flex flex-col gap-3">
          <SkBar h="h-4" w="w-20" />
          <SkBar h="h-3" w="w-28" />
          <SkBar h="h-10" w="w-full" className="rounded-md" />
          <SkBar h="h-3" w="w-28" />
          <SkBar h="h-10" w="w-full" className="rounded-md" />
        </div>
      ))}
    </div>
    <div className="flex justify-end">
      <SkBar h="h-10" w="w-40" className="rounded-md" />
    </div>
  </Card>
);

/* ─────────── Generic form tab fallback ─────────── */

const GenericFormTabContent = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <SectionHeader />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <SkBar h="h-3" w="w-24" />
          <InputField />
        </div>
      ))}
    </div>
    <div className="flex justify-end mt-6">
      <SkBar h="h-10" w="w-32" className="rounded-md" />
    </div>
  </Card>
);

/* ─────────── Main loader ─────────── */

const SettingsPageLoader = () => {
  const { search } = useLocation();
  const tabParam = new URLSearchParams(search).get('tab');
  const urlIndex = tabParam !== null && !Number.isNaN(Number(tabParam)) ? Number(tabParam) : 0;
  const activeIndex = URL_TO_VISUAL[urlIndex] ?? 0;

  let content;
  if (urlIndex === 0) content = <GeneralTabContent />;
  else if (urlIndex === 3) content = <TeamAccessTabContent />;
  else if (urlIndex === 4) content = <RolesTabContent />;
  else if (urlIndex === 5) content = <ProfileTabContent />;
  else if (urlIndex === 6) content = <LanguageTabContent />;
  else if (urlIndex === 7) content = <LoginConfigTabContent />;
  else content = <GenericFormTabContent />;

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <SettingsHeader />

      {/* Tabs in their own card */}
      <Card className="shadow-sm mb-4 sm:mb-6" styles={{ body: { padding: '12px 20px 0' } }}>
        <TabsRow active={activeIndex} />
      </Card>

      {/* Content renders its own cards */}
      {content}
    </div>
  );
};

export default SettingsPageLoader;
